import React, { useEffect, useState } from "react"; // eslint-disable-line no-unused-vars
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Check, ExternalLink, ShieldCheck, Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SEO from "@/components/app/SEO";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const MarkdownLink = ({ href, children }) => {
  const url = href || "";
  const internal = url.match(/^https?:\/\/resiliationexpresse\.fr(\/.*)?$/);
  if (internal) return <Link to={internal[1] || "/"} className="text-primary hover:underline" data-testid="md-internal-link">{children}</Link>;
  if (url.startsWith("/")) return <Link to={url} className="text-primary hover:underline" data-testid="md-internal-link">{children}</Link>;
  return <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{children}</a>;
};
const MD_COMPONENTS = { a: MarkdownLink };

const METHOD_LABELS = {
  espace_client: "Espace client",
  resiliation_en_ligne: "Résiliation en ligne",
  quelques_clics: "En quelques clics",
  email: "Email",
  telephone: "Téléphone",
  agence: "En agence",
  lettre_simple: "Lettre simple",
  recommande: "Lettre recommandée",
  nouvel_assureur: "Via nouvel assureur",
  autre: "Autre canal",
};

export default function OrganizationPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/organizations/${slug}`).then((r) => setOrg(r.data)).catch(() => setOrg(false));
  }, [slug]);

  // Fetch 3 related orgs from the same primary category
  useEffect(() => {
    if (!org || org === false) return;
    const cat = (org.categories || [])[0];
    if (!cat) return;
    api.get(`/organizations?category=${cat}&limit=8`)
      .then((r) => {
        const items = Array.isArray(r.data?.items) ? r.data.items : [];
        setRelated(items.filter((o) => o.slug !== org.slug).slice(0, 3));
      })
      .catch(() => setRelated([]));
  }, [org]);

  // JSON-LD Organization + BreadcrumbList schema — runs before any early return
  useEffect(() => {
    if (!org || org === false || !org.slug) return;
    const url = `https://resiliationexpresse.fr/organisme/${org.slug}`;
    const jsonld = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": org.name,
        "url": org.website || url,
        "description": org.seo_intro || org.description || "",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://resiliationexpresse.fr/" },
          { "@type": "ListItem", "position": 2, "name": "Annuaire", "item": "https://resiliationexpresse.fr/annuaire" },
          { "@type": "ListItem", "position": 3, "name": org.name, "item": url },
        ],
      },
    ];
    let el = document.getElementById("org-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "org-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(jsonld);
    return () => { const e = document.getElementById("org-jsonld"); if (e) e.remove(); };
  }, [org]);

  if (org === false) return <div className="mx-auto max-w-7xl p-8">Organisme introuvable.</div>;
  if (!org) return <div className="mx-auto max-w-7xl p-8">Chargement...</div>;

  const startCase = async () => {
    if (!user) {
      nav(`/connexion?redirect=/organisme/${slug}`);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/cases", {
        organization_id: org.id,
        organization_name: org.name,
        category_slug: (org.categories || [])[0] || "",
      });
      toast.success("Dossier créé");
      nav(`/resilier/${data.id}`);
    } catch (e) {
      toast.error("Impossible de créer le dossier");
    } finally {
      setLoading(false);
    }
  };

  const seoTitle = org.seo_title || `Résilier ${org.name}`;
  const seoDescription = org.seo_intro || `Comment résilier ${org.name} : adresse, préavis, méthodes, motifs et justificatifs à jour — assistance et envoi en recommandé disponibles.`;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
      <SEO title={seoTitle} description={seoDescription} canonical={`/organisme/${org.slug}`} noindex={!org.seo_enriched} />
      <Link to="/annuaire" className="text-sm text-muted-foreground hover:text-foreground">← Retour à l'annuaire</Link>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <h1 className="text-4xl font-bold tracking-tight">{org.name}</h1>
              {org.trust_level === "verified" && (
                <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300 border-0">
                  <ShieldCheck className="h-3 w-3 mr-1" />Vérifié
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground leading-relaxed max-w-3xl">{org.description}</p>
            {org.website && (
              <a href={org.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary mt-3">
                <ExternalLink className="h-3 w-3" /> Site officiel
              </a>
            )}
          </div>

          {org.can_terminate_free_directly && (
            <Card className="p-5 border-green-200 dark:border-green-900 bg-green-50/40 dark:bg-green-950/20">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-green-700 dark:text-green-400 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold mb-1">Résiliation gratuite disponible</div>
                  <div className="text-sm text-muted-foreground">Cet organisme propose la résiliation directement depuis votre espace client. Vous pouvez le faire vous-même, gratuitement.</div>
                </div>
              </div>
            </Card>
          )}

          <div className="card-elevated p-6">
            <h2 className="text-xl font-semibold mb-4">Modes de résiliation disponibles</h2>
            <div className="grid grid-cols-2 gap-3">
              {(org.termination_methods || []).map((m) => (
                <div key={m} className="flex items-center gap-2 text-sm" data-testid={`method-${m}`}>
                  <Check className="h-4 w-4 text-green-600" />
                  {METHOD_LABELS[m] || m}
                </div>
              ))}
            </div>
          </div>

          <div className="card-elevated p-6">
            <h2 className="text-xl font-semibold mb-4">Informations pratiques</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><dt className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Préavis</dt><dd>{org.notice_period || "Variable selon contrat"}</dd></div>
              <div><dt className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Reconduction</dt><dd>{org.renewal || "Tacite (à confirmer)"}</dd></div>
              <div><dt className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Adresse de résiliation</dt><dd>{org.termination_address || "À définir selon votre contrat"}</dd></div>
              <div><dt className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Dernière vérification</dt><dd className="font-mono text-xs">{org.last_verified_at ? new Date(org.last_verified_at).toLocaleDateString("fr-FR") : "—"}</dd></div>
            </dl>
          </div>

          {org.seo_content && (
            <div className="card-elevated p-6" data-testid="org-seo-content">
              <div className="prose dark:prose-invert max-w-none leading-relaxed">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                  {org.seo_content}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {related.length > 0 && (
            <div className="card-elevated p-6" data-testid="org-related">
              <h2 className="text-xl font-semibold mb-4">Autres organismes similaires</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {related.map((r) => (
                  <Link key={r.slug} to={`/organisme/${r.slug}`} className="border border-border rounded-lg p-4 hover:border-primary transition-colors" data-testid={`related-${r.slug}`}>
                    <div className="font-semibold text-sm mb-1">{r.name}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2">{r.description}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <Card className="p-6 sticky top-24">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Résilier maintenant</div>
            <p className="text-sm text-muted-foreground mb-6">
              Notre assistant vous guide pas à pas pour générer votre courrier et l'envoyer en recommandé.
            </p>
            <Button
              onClick={startCase}
              disabled={loading}
              className="w-full bg-gradient-brand text-white border-0 hover:opacity-90"
              data-testid="start-case-btn"
            >
              {loading ? "Création..." : "Commencer ma résiliation"}
            </Button>
            <div className="mt-4 text-xs text-muted-foreground text-center">
              À partir de 9,90 € TTC en envoi assisté · Offre sur mesure disponible
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
